import {monitorForElements} from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import React, {useEffect} from 'react';
import {flushSync} from 'react-dom';
import {extractClosestEdge} from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import {Box, Typography} from '@mui/material';
import {reorderWithEdge} from '@atlaskit/pragmatic-drag-and-drop-hitbox/util/reorder-with-edge';
import {triggerPostMoveFlash} from '@atlaskit/pragmatic-drag-and-drop-flourish/trigger-post-move-flash';
import {DraggableItem} from "./DraggableItem";

interface DraggableTableProps {
    items: any[];
    onReorder?: (items: any[]) => void;
    children?: (item: any, index: number) => React.ReactNode;
}

export function DraggableTable({items, onReorder, children}: Readonly<DraggableTableProps>) {
    useEffect(() => {
        return monitorForElements({
            canMonitor: ({source}) => source.data.type === 'item',
            onDrop: ({location, source}) => {
                const target = location.current.dropTargets[0];
                if (!target) return;

                const sourceId = source.data.id as number;
                const targetId = target.data.id as number;
                const closestEdge = extractClosestEdge(target.data);

                flushSync(() => {
                    const reorderedItems = reorderWithEdge({
                        list: items,
                        startIndex: sourceId,
                        indexOfTarget: targetId,
                        closestEdgeOfTarget: closestEdge,
                        axis: 'vertical',
                    });
                    onReorder?.(reorderedItems);
                });

                if (source.element) {
                    triggerPostMoveFlash(source.element as HTMLElement);
                }
            },
        });
    }, [items, onReorder]);

    return (
        <Box sx={{p: 1}}>
            <Box sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
                {items.map((item, index) => (
                    <DraggableItem key={index} item={item} itemId={index}>
                        {children ? children(item, index) : <Typography>{item.content}</Typography>}
                    </DraggableItem>
                ))}
            </Box>
        </Box>
    );
}
